import {
  QueryCondition,
  QueryOperation,
  SearchQuery,
} from "../../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { Answer, QuestionAnswer } from "../../repository/data.default";
import { InputObject } from "../../controls/default";
import { Dayjs } from "dayjs";
import {
  getQuestionnaireAllSections,
  getSectionAllQuestions,
  getQuestionAllChoices,
  getQuestionnaireQuestionAnswers,
} from "../../repository/data.repository";
import { QuestionnaireQuestion, QuestionnaireQuestionChoice, QuestionnaireSection } from "../../repository/data.default";

export interface AnswersSearchForm {
  readonly user: InputObject;
  readonly questionnaire: InputObject;
  readonly bundle: InputObject;
  readonly company: InputObject;
  readonly status: InputObject;
  readonly createdOn: Dayjs[];
  readonly completedAt: Dayjs[];
}

// Failed scoring attempts after which the platform stops retrying an answer (the backend's
// QuestionnaireScoreCalculator.MaxAttempts) — "Scoring failed" is filtered against it.
const MAX_SCORING_ATTEMPTS = 5;

/** An answer's lifecycle, as an operator reads it. */
export const AnswerStatus = {
  InProgress: "In progress",
  AwaitingScoring: "Awaiting scoring",
  Scored: "Scored",
  ScoringFailed: "Scoring failed",
} as const;
type AnswerStatusKey = keyof typeof AnswerStatus;

export const answerStatusOf = (answer: Answer): AnswerStatusKey =>
  !answer.completed
    ? "InProgress"
    : answer.scoringCompleted
      ? "Scored"
      : answer.scoringFailed
        ? "ScoringFailed"
        : "AwaitingScoring";

// Each status as filter conditions on plain (filterable) fields; ScoringFailed is computed, so it is
// expressed through scoringAttempts.
const statusConditions: Record<AnswerStatusKey, QueryCondition[]> = {
  InProgress: [{ field: "completed", operation: QueryOperation.Equal, values: [false] }],
  AwaitingScoring: [
    { field: "completed", operation: QueryOperation.Equal, values: [true] },
    { field: "scoringCompleted", operation: QueryOperation.Equal, values: [false] },
    { field: "scoringAttempts", operation: QueryOperation.LessThan, values: [MAX_SCORING_ATTEMPTS] },
  ],
  Scored: [{ field: "scoringCompleted", operation: QueryOperation.Equal, values: [true] }],
  ScoringFailed: [
    { field: "completed", operation: QueryOperation.Equal, values: [true] },
    { field: "scoringCompleted", operation: QueryOperation.Equal, values: [false] },
    { field: "scoringAttempts", operation: QueryOperation.GreaterThanOrEqual, values: [MAX_SCORING_ATTEMPTS] },
  ],
};

export const AnswersColumns: DataColumn[] = [
  {
    title: "User Name",
    key: "userUserName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Questionnaire",
    key: "questionnaireName",
    width: 192,
    sort: true,
  },
  {
    title: "Bundle",
    key: "bundleName",
    width: 192,
    sort: true,
  },
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
  },
  {
    title: "Status",
    key: "completed",
    width: 144,
    transform: (_: any, record: Answer) => AnswerStatus[answerStatusOf(record)],
  },
  {
    title: "Started On",
    key: "createdOn",
    width: 128,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  {
    // The moment an answer's results are reported against.
    title: "Completed On",
    key: "completedAt",
    width: 128,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
];

const answersQueryMappings: QueryFieldMapping[] = [
  { formField: "user", queryField: "userUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "questionnaire", queryField: "questionnaireUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "bundle", queryField: "bundleUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "completedAt", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapAnswersQuery = (values: AnswersSearchForm): SearchQuery => {
  const query = buildSearchQuery(values, answersQueryMappings);
  const status = values?.status?.value as AnswerStatusKey | undefined;
  return status && statusConditions[status]
    ? { ...query, conditions: [...query.conditions, ...statusConditions[status]] }
    : query;
};

interface AnswersMap {
  [key: string]: QuestionAnswer;
}

export async function fetchQuestionnaireAnswerData(
  questionnaireUid: string,
  questionnaireAnswerUid: string,
): Promise<{ sections: any[] }> {
  try {
    const answersResponse = await getQuestionnaireQuestionAnswers(questionnaireAnswerUid);
    const answersMap = answersResponse.data.reduce((acc: AnswersMap, answer: QuestionAnswer) => {
      acc[answer.questionUid] = answer; 
      return acc;
    }, {});

    const sectionsData = await getQuestionnaireAllSections(questionnaireUid);

    const processedSections = await Promise.all(
      sectionsData.data.map(async (section) => {
        const questionsData = await getSectionAllQuestions(section.uid);
        const sortedQuestions = [...questionsData.data].sort(
          (a, b) => a.order - b.order,
        );

        const processedQuestions = await Promise.all(
          sortedQuestions.map(async (question) => {

            const choicesData = await getQuestionAllChoices(question.uid);
            const sortedChoices = [...choicesData.data].sort(
              (a, b) => a.order - b.order,
            );

            const processedChoices = sortedChoices.map( (choice) => {

              return {
                value: choice.uid,
                label: choice.description,
              };
            })

            // A question may have no answer row: a data-collection form can be completed with categories left
            // untouched ("nothing to report"), and an optional question can be skipped. Show it unanswered.
            const questionAnswer: QuestionAnswer | undefined = answersMap[question.uid];

            return {
              uid: question.uid,
              questionType: question.questionType,
              description: question.description,
              order: question.order,
              rowVersion: question.rowVersion,
              isRequired: question.isRequired,
              choices: processedChoices,
              answers: questionAnswer?.selectedChoices ?? [],
              answerText: questionAnswer?.freeTextAnswer,
              numericAnswer: questionAnswer?.numericAnswer,
              answeredAt: questionAnswer?.createdOn,
            };
          }),
        );

        return {
          uid: section.uid,
          name: section.name,
          description: section.description,
          rowVersion: section.rowVersion,
          questions: processedQuestions,
        };
      }),
    );

    return {
      sections: processedSections,
    };
  } catch (error) {
    console.error("Error fetching questionnaire data:", error);
    throw error;
  }
}

export interface AnswerQuestionValues extends Omit<QuestionnaireQuestion, "sectionUid" | "choicesCount" | "translationsCount"> {
  readonly questionType: "FreeText" | "SingleChoice" | "MultipleChoice" | "Numeric";
  readonly description: string;
  readonly order: number;
  readonly rowVersion: string;
  readonly answers?: string[];
  readonly answerText?: string;
  readonly numericAnswer?: number;
  readonly answeredAt: string;
  readonly choices: Array<{
    value: string;
    label: string;
  }>
}

export interface InitialAnswerValues
  extends Pick<Answer, "userUserName" | "createdOn" | "bundleName" | "questionnaireName" | "companyName"> {
  /** Formatted for display ("—" while in progress). */
  completedAt?: string;
  /** The operator-facing status label. */
  status?: string;
  sections?: Array<
    Omit<QuestionnaireSection, "questionsCount" | "questionnaireUid"> & {
      questions?: Array<
        Omit<
          QuestionnaireQuestion,
          "isRequired" | "sectionUid" | "choicesCount" | "translationsCount"
        > & {
          choices?: Array<
            Omit<
              QuestionnaireQuestionChoice,
              "questionUid" | "translationsCount"
            > & {
              translations?: Array<{
                uid: string;
                language: string;
                text: string;
                rowVersion: string;
              }>;
            }
          >;
          translations?: Array<{
            uid: string;
            language: string;
            text: string;
            rowVersion: string;
          }>;
        }
      >;
    }
  >;
}
